import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import TableSelector from './TableSelector';
import AssignMeats from './AssignMeats';
import config from '../config';
import "./ReservationForm.css";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { useIsMobile } from '../Responsive';
import { formatDateTimeGR } from './shared/dateUtils';
import Modal from 'react-modal';
import moment from 'moment';
import './Clients.css';
import { useTranslation } from "react-i18next";
import { Greek } from "flatpickr/dist/l10n/gr.js";
import ClientsHistory from "./ClientsHistory";
import AvailabilityCalendar from './AvailabilityCalendar';

import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";

import i18n from "../i18n";
const tr = i18n.t.bind(i18n);
// Build 12:00 24:00 (144 ? 5') seat usage from /tables/calendar data
// tableAvailability is the response from GET /tables/calendar/:date
const buildSeatGraphData = (tableAvailability, reservationDurationMinutes = 120) => {
  if (!Array.isArray(tableAvailability) || tableAvailability.length === 0) {
    return { totalCapacity: 0, slots: [], peakReserved: 0 };
  }

  // Y-axis max: sum of all table capacities
  const totalCapacity = tableAvailability.reduce(
    (sum, table) => sum + (Number(table.capacity) || 0),
    0
  );

  // Collect unique reservations by id so we don't double-count people
  const reservationMap = new Map();

  tableAvailability.forEach((table) => {
    (table.reservations || []).forEach((res) => {
      if (!res || res.id == null) return;
      if (reservationMap.has(res.id)) return; // seen via another table

      let hour = 0;
      let minute = 0;

      if (typeof res.arrival === "string") {
        // "YYYY-MM-DDTHH:mm..."
        hour = parseInt(res.arrival.slice(11, 13), 10) || 0;
        minute = parseInt(res.arrival.slice(14, 16), 10) || 0;
      } else {
        const d = new Date(res.arrival);
        hour = d.getHours();
        minute = d.getMinutes();
      }

      const WINDOW_START = 12 * 60;   // 12:00 in minutes from midnight
      const WINDOW_END   = 24 * 60;   // 24:00

      // Start time in minutes from midnight
      let start = hour * 60 + minute;
      const rawEnd = start + (Number(reservationDurationMinutes) || 120);

      // Completely outside our 12 24 window > skip
      if (rawEnd <= WINDOW_START || start >= WINDOW_END) return;

      // Clamp to the 12 24 window
      start = Math.max(start, WINDOW_START);
      const end = Math.min(rawEnd, WINDOW_END);

      reservationMap.set(res.id, {
        start,
        end,
        people: Number(res.people) || 0,
      });
    });
  });

  // 5-minute resolution
  const SLOT_MINUTES = 5;
  const DAY_START = 12 * 60;
  const SLOT_COUNT = (24 * 60 - DAY_START) / SLOT_MINUTES; // 144 slots

  const slots = [];
  let peakReserved = 0;

  for (let i = 0; i < SLOT_COUNT; i++) {
    const slotStart = DAY_START + i * SLOT_MINUTES;
    const slotEnd = slotStart + SLOT_MINUTES;

    let reservedSeats = 0;

    reservationMap.forEach((res) => {
      const overlaps = res.start < slotEnd && res.end > slotStart;
      if (overlaps) reservedSeats += res.people;
    });

    if (reservedSeats > totalCapacity) reservedSeats = totalCapacity;
    if (reservedSeats > peakReserved) peakReserved = reservedSeats;

    const h = Math.floor(slotStart / 60);
    const m = slotStart % 60;
    const label = tr('{{var1}}:{{var2}}', {
      var1: String(h).padStart(2, "0"),
      var2: String(m).padStart(2, "0")
    });

    slots.push({ label, value: reservedSeats });
  }

  return { totalCapacity, slots, peakReserved };
};

// Small helper: one-line horizontal scroll with arrow hints (mobile only)
const ScrollRow = ({ children, className = '', resetKey = 0, ariaLabel = '' }) => {
  const viewportRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    el.scrollLeft = 0;
    requestAnimationFrame(update);
  }, [resetKey, update]);

  useEffect(() => {
    update();
    const onResize = () => update();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [update]);

  return (
    <div className={`ts-scroll-wrap ${canLeft ? 'can-left' : ''} ${canRight ? 'can-right' : ''}`}>
      <button
        type="button"
        className="ts-scroll-arrow left"
        aria-label="Scroll left"
        onClick={() => viewportRef.current?.scrollBy({ left: -160, behavior: 'smooth' })}
      >
        {'\u2039'}
      </button>

      <div
        className={`ts-scroll-viewport ${className}`}
        ref={viewportRef}
        onScroll={update}
        aria-label={ariaLabel}
      >
        {children}
      </div>

      <button
        type="button"
        className="ts-scroll-arrow right"
        aria-label="Scroll right"
        onClick={() => viewportRef.current?.scrollBy({ left: 160, behavior: 'smooth' })}
      >
        {'\u203A'}
      </button>
    </div>
  );
};



//==============================================================================================================

const ReservationForm = ({ reservation, assignedTables, onClose, forceBottomSelector }) => {

  const [phone, setPhone] = useState('');
  const [isNewClient, setIsNewClient] = useState(true);
  const [clientOptions, setClientOptions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedTables, setSelectedTables] = useState(assignedTables || []);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [isMeatsModalOpen, setIsMeatsModalOpen] = useState(false);
  const [createdReservation, setCreatedReservation] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [reservationDurationMinutes, setReservationDurationMinutes] = useState(120);
  
  const clientSearchSeqRef = useRef(0);
  const formRootRef = useRef(null);
  
  // Availability calendar uses 5-minute slots
  const SLOT_MINUTES = 5;
  
  // How many 5' slots this company reservation lasts
  const reservationDurationSlots = React.useMemo(() => {
    const d = Number(reservationDurationMinutes) || 120;
    return Math.max(1, Math.ceil(d / SLOT_MINUTES));
  }, [reservationDurationMinutes]);
  
  // Keep your old visual "-1 slot" style (because CSS used 23 for 24 slots)
  const reservationBlockSlotsForWidth = Math.max(1, reservationDurationSlots - 1);
  
  // CSS widths for the overlay blocks
  const reservationBlockWidth = `calc(${reservationBlockSlotsForWidth} * 5px - 3px)`;
  const hoverBlockWidth = `calc(${reservationBlockSlotsForWidth} * 5px - 1px)`;  
  
  
  const mountedRef = useRef(true);
  
  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  const [planId, setPlanId] = useState(null);
  const isStarterPlan = planId === 1;

  const isMobile = useIsMobile();
  const handleCloseMeatsModal = () => {
    setIsMeatsModalOpen(false);
    navigate('/reservations-list');   // go back and let list refetch on mount
  };
  
  const { i18n: i18nextI18n } = useTranslation();
  const isGreek = (i18nextI18n.resolvedLanguage || i18nextI18n.language || "en")
    .split("-")[0] === "el";

  // helpers for date/time logic
  const isSameLocalDay = (d1, d2) =>
    !!d1 && !!d2 &&
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

  // compute next 5' slot from "now" but never before 12:00
  const next5SlotToday = () => {
    const now = new Date();
    let h = now.getHours();
    let m = Math.ceil(now.getMinutes() / 5) * 5;
    if (m === 60) { h += 1; m = 0; }
    if (h < 12) { h = 12; m = 0; }
    const hhmm = tr('{{var1}}:{{var2}}', {
      var1: String(h).padStart(2,'0'),
      var2: String(m).padStart(2,'0')
    });
    return { h, m, hhmm };
  };

  // Parse 'YYYY-MM-DD' as a LOCAL date (avoid UTC shift)
  const parseYmdLocal = (ymd) => {
    if (!ymd) return null;
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(y, m - 1, d); // local midnight
  };

  // Today in local time as 'YYYY-MM-DD'
  const todayLocalISO = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return tr('{{y}}-{{m}}-{{d}}', {
      y,
      m,
      d
    });
  };

  const today = todayLocalISO();

  const [calendarInfo, setCalendarInfo] = useState({});

  const [areaFilters, setAreaFilters] = useState({
    vip: false, fireplace: false, smoking: false, inside: false, outside: false, view: false, garden: false,
  });

  const [areasById, setAreasById] = useState({});
  const [selectedAreaId, setSelectedAreaId] = useState(null);

  const ALL_FILTERS = [
    ['garden','Garden'],
    ['smoking','Smoking'],
    ['outside','Outside'],
    ['inside','Inside'],
    ['view','View'],
    ['fireplace','Fireplace'],
    ['vip','VIP']
  ];

  const visibleFilters = React.useMemo(() => {
    const areasArr = Object.values(areasById);
    if (!areasArr.length) return ALL_FILTERS; // while loading, show all to avoid UI jump
    const hasAny = (k) => areasArr.some(a => !!a[k]);
    return ALL_FILTERS.filter(([k]) => hasAny(k));
  }, [areasById]);

  const [availabilityDate, setAvailabilityDate] = useState(today);
  const [availabilityData, setAvailabilityData] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);

  // NEW: mini capacity graph data for the reservation date
  const [seatGraphData, setSeatGraphData] = useState(null);
  const [selectedSlotForGraph, setSelectedSlotForGraph] = useState(null);

  const containerRef = React.useRef(null);
  const tbodyRef = React.useRef(null);
  const hoverRef = React.useRef(null);

  // 12:00 is index 0; each index is +5 minutes
  const timeFromIndex = (i) => {
    const totalMinutes = 12 * 60 + i * 5;
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return tr('{{var1}}:{{var2}}', {
      var1: String(h).padStart(2,'0'),
      var2: String(m).padStart(2,'0')
    });
  };

  useEffect(() => {
    setSelectedTables(Array.isArray(assignedTables) ? assignedTables : []);
  }, [reservation?.id, assignedTables]);

  useEffect(() => {
    if (!isMobile) return;
    if (!reservation?.id) return;          // only editing existing reservations
    if (!forceBottomSelector) return;      // only when opened via modal path
  
    // If we're inside ReactModal, scroll its content to top so TableSelector has sane rect.top
    const modalContent = formRootRef.current?.closest('.ReactModal__Content');
    if (modalContent && typeof modalContent.scrollTo === 'function') {
      modalContent.scrollTo({ top: 0, behavior: 'auto' });
    } else if (modalContent) {
      modalContent.scrollTop = 0;
    }
  }, [isMobile, reservation?.id, forceBottomSelector]);

  useEffect(() => {
    axios
      .get(tr('{{API_BASE_URL}}/users/plan-permissions', {
      API_BASE_URL: config.API_BASE_URL
    }))
      .then((res) => {
        const { active, planId: rawPlanId } = res.data || {};
        if (!active || rawPlanId == null) return;
        setPlanId(Number(rawPlanId));
      })
      .catch((err) => {
        console.error("Error fetching plan info in Reservation Form:", err);
      });
  }, []);

  useEffect(() => {
    let cancelled = false;
  
    axios
      .get(
        tr("{{API_BASE_URL}}/_companies/settings", { API_BASE_URL: config.API_BASE_URL }),
        { withCredentials: true, timeout: 15000 }
      )
      .then((res) => {
        if (cancelled) return;
        const v = Number(res?.data?.reservation_duration);
        setReservationDurationMinutes(Number.isFinite(v) && v > 0 ? v : 120);
      })
      .catch(() => {
        // keep default 120 if endpoint fails
      });
  
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!showAvailabilityModal) return; // only when modal is open
    const tbody = tbodyRef.current;
    const overlay = hoverRef.current;
    const container = containerRef.current;
    if (!tbody || !overlay || !container) return;

    const onMove = (e) => {
      const cell = e.target.closest('td.slot');
      if (!cell || !tbody.contains(cell)) {
        overlay.style.display = 'none';
        return;
      }
      // Compute overlay position relative to the scroll container
      const cellRect = cell.getBoundingClientRect();
      const contRect = container.getBoundingClientRect();
      const x = cellRect.left - contRect.left + container.scrollLeft;
      const y = cellRect.top  - contRect.top  + container.scrollTop;

      // Get slot index (first column is table-name)
      const slotIndex = cell.cellIndex - 1;
      // Table number from the row's first cell
      const row = cell.parentElement;
      const tableNum = row?.children?.[0]?.textContent?.trim().split(' ')[0] ?? '';

      overlay.style.display = 'block';
      overlay.style.transform = tr('translate({{x}}px, {{y}}px)', {
        x,
        y
      });
      overlay.querySelector('.hover-text').textContent = tr('{{tableNum}}) {{var2}}', {
        tableNum,
        var2: timeFromIndex(slotIndex)
      });
    };
    const onLeave = () => { overlay.style.display = 'none'; };

    tbody.addEventListener('mousemove', onMove, { passive: true });
    tbody.addEventListener('mouseleave', onLeave);
    return () => {
      tbody.removeEventListener('mousemove', onMove);
      tbody.removeEventListener('mouseleave', onLeave);
    };
  }, [showAvailabilityModal, availabilityData, selectedAreaId, areaFilters]);

  const areasLoaded = Object.keys(areasById).length > 0;

  // Helper: which filters are ON?
  const activeFilterKeys = () => Object.entries(areaFilters).filter(([,v]) => v).map(([k]) => k);

  // Helper: does an area match all active filters?
  const areaMatchesFilters = (areaIdRaw) => {
    const areaId = areaIdRaw != null ? Number(areaIdRaw) : null;
    const keys = activeFilterKeys();
    if (keys.length === 0) return true;          // no filters -> show all
    if (!areasLoaded) return true;                // wait until areas load
    const a = areaId != null ? areasById[areaId] : undefined;
    if (!a) return false;                         // unknown area after load
    return keys.every((k) => !!a[k]);
  };

  useEffect(() => {
    // fetch areas to build map { [id]: {vip, fireplace, ...} }
    axios.get(tr('{{API_BASE_URL}}/areas', {
      API_BASE_URL: config.API_BASE_URL
    }))
      .then(res => {
        const map = {};
        (res.data || []).forEach(a => { if (a?.id) map[a.id] = a; });
        setAreasById(map);
      })
      .catch(err => console.error('Failed to load areas for filters', err));
  }, []);

  const fetchAvailability = async (selectedDate) => {
    setLoadingAvailability(true);
    try {
        const response = await axios.get(tr('{{API_BASE_URL}}/tables/calendar/{{selectedDate}}', {
          API_BASE_URL: config.API_BASE_URL,
          selectedDate
        }));
        const reservations = {};
        const info = {};
        response.data.forEach(t => {
          t.reservations.forEach(r => {
            let date = r.arrival.split('T')[0];
            if(!info[date])
              info[date] = {reservations: 0, tables: 0, attendees: 0};
            if(!reservations[r.id]){
              info[date].reservations++;
              info[date].attendees = info[date].attendees + r.people;
            }
            info[date].tables++;
            reservations[r.id] = true;
          });
        })
        setCalendarInfo(info);
        setAvailabilityData(response.data);
    } catch (error) {
        console.error("Error fetching table availability:", error);
    }
    setLoadingAvailability(false);
  };

  const navigate = useNavigate();

  const [clientForm, setClientForm] = useState({
    firstname: '',
    lastname: '',
    phone: '',
    email: '', 
    comment: '',
    rate: 3,      // ? default rating
    id: null,
  });

  const [historyTotals, setHistoryTotals] = useState(null);

  // helper: at least one identity field present
  const isClientIdentified = !!(clientForm.phone || clientForm.firstname || clientForm.lastname);

  const [reservationDetails, setReservationDetails] = useState(() => {
    const now = new Date();                      // same logic as next5SlotToday()
    let h = now.getHours(), m = Math.ceil(now.getMinutes()/5)*5;
    if (m === 60) { h += 1; m = 0; }
    if (h < 12) { h = 12; m = 0; }
    const hhmm = tr('{{var1}}:{{var2}}', {
      var1: String(h).padStart(2,'0'),
      var2: String(m).padStart(2,'0')
    });
    return {
      date: today,
      baseHours: h,
      baseMinutes: m,
      addedHours: 0,
      addedMinutes: 0,
      people: 2,
      comment: '',
      reservationTime: hhmm,
    };
  });
  
  const [peopleDraft, setPeopleDraft] = useState(String(reservationDetails.people ?? 2));

  const livePeople = React.useMemo(() => {
    const v = parseInt(peopleDraft, 10);
    return Number.isFinite(v) ? v : 0;
  }, [peopleDraft]);

  const selectorKey = React.useMemo(() => {
    // changing this forces TableSelector to remount => activeTab resets to 0 (Layout)
    return tr('ts-{{d}}-{{t}}-{{mobile}}', {
      d: reservationDetails.date,
      t: reservationDetails.reservationTime,
      mobile: isMobile ? 1 : 0
    });
  }, [reservationDetails.date, reservationDetails.reservationTime, isMobile]);


  // NEW: whenever the reservation date changes, fetch seat usage for that day
  useEffect(() => {
    const selectedDate = reservationDetails?.date;
    if (!selectedDate) {
      setSeatGraphData(null);
      return;
    }
  
    let cancelled = false;
  
    Promise.all([
      axios.get(tr('{{API_BASE_URL}}/tables', { API_BASE_URL: config.API_BASE_URL })),
      axios.get(tr('{{API_BASE_URL}}/reservations/calendar/{{selectedDate}}', {
        API_BASE_URL: config.API_BASE_URL,
        selectedDate
      }))
    ])
      .then(([tablesResp, resResp]) => {
        if (cancelled) return;
  
        const tables = tablesResp?.data || [];
        const reservations = resResp?.data || [];
  
        const totalCapacity = tables.reduce(
          (sum, t) => sum + (Number(t.capacity) || 0),
          0
        );
  
        // Build a virtual single "table" that carries totalCapacity and all reservations.
        // buildSeatGraphData already deduplicates by reservation id.
        const virtual = [{
          capacity: totalCapacity,
          reservations
        }];
  
        const graph = buildSeatGraphData(virtual, reservationDurationMinutes);
        setSeatGraphData(graph);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error fetching seat capacity data:", err);
        setSeatGraphData(null);
      });
  
    return () => { cancelled = true; };
  }, [reservationDetails.date, reservationDurationMinutes]);


  // Keep the graph cursor (vertical line) synced with the current reservation time
  useEffect(() => {
    if (
      !seatGraphData ||
      !seatGraphData.slots ||
      seatGraphData.slots.length === 0
    ) {
      setSelectedSlotForGraph(null);
      return;
    }

    const timeStr = reservationDetails?.reservationTime;
    if (!timeStr) {
      setSelectedSlotForGraph(null);
      return;
    }

    const [hhStr, mmStr] = timeStr.split(":");
    const h = parseInt(hhStr, 10);
    const m = parseInt(mmStr, 10);
    if (Number.isNaN(h) || Number.isNaN(m)) {
      setSelectedSlotForGraph(null);
      return;
    }

    const normalized = tr('{{var1}}:{{var2}}', {
      var1: String(h).padStart(2, "0"),

      var2: String(m).padStart(
        2,
        "0"
      )
    });

    // Our slots are every 5' from 12:00..24:00, labeled "HH:MM"
    const slot = seatGraphData.slots.find((s) => s.label === normalized);
    if (!slot) {
      setSelectedSlotForGraph(null);
      return;
    }

    setSelectedSlotForGraph(slot);
  }, [seatGraphData, reservationDetails.reservationTime]);

  // Dropdown sources for time (include current selection even if off-grid)
  const HOURS = React.useMemo(() => {
    const base = Array.from({ length: 12 }, (_, i) => 12 + i); // 12..23
    const cur = Number(reservationDetails.baseHours ?? 12);
    return base.includes(cur) ? base : [...base, cur].sort((a, b) => a - b);
  }, [reservationDetails.baseHours]);

  const MINUTES = React.useMemo(() => {
    const base = Array.from({ length: 12 }, (_, i) => i * 5); // 0..55
    const cur = Number(reservationDetails.baseMinutes ?? 0);
    return base.includes(cur) ? base : [...base, cur].sort((a, b) => a - b);
  }, [reservationDetails.baseMinutes]);

  // Added time dropdowns
  const ADDED_HOURS = React.useMemo(() => Array.from({ length: 9 }, (_, i) => i), []); // 0..8
  const ADDED_MINUTES = React.useMemo(() => Array.from({ length: 12 }, (_, i) => i * 5), []); // 0..55 in 5'

  const setTimeHM = (h, m) => {
    const hh = String(h).padStart(2, '0');
    const mm = String(m).padStart(2, '0');
    setReservationDetails(prev => {
      return ({
        ...prev,
        baseHours: h,
        baseMinutes: m,
        addedHours: 0,
        addedMinutes: 0,

        reservationTime: tr('{{hh}}:{{mm}}', {
          hh,
          mm
        })
      });
    });
  };

  const handleHourChange = (e) => {
    const h = Math.min(23, Math.max(12, parseInt(e.target.value, 10) || 12));
    setTimeHM(h, reservationDetails.baseMinutes);
  };

  const handleMinuteChange = (e) => {
    const m = Math.min(55, Math.max(0, parseInt(e.target.value, 10) || 0));
    setTimeHM(reservationDetails.baseHours, m);
  };

  // Added-time handlers (dropdowns)   do NOT mutate base hour/min here.
  // We keep added values as selected and compute the final display from base+added.
  const handleAddedHoursChange = (e) => {
    const ah = Math.min(8, Math.max(0, parseInt(e.target.value, 10) || 0));
    setReservationDetails(prev => ({ ...prev, addedHours: ah }));
  };

  const handleAddedMinutesChange = (e) => {
    const am = Math.min(55, Math.max(0, parseInt(e.target.value, 10) || 0));
    setReservationDetails(prev => ({ ...prev, addedMinutes: am }));
  };

  // Compute the final (displayed) arrival time = base + added
  const finalHM = React.useMemo(() => {
    const bh = reservationDetails.baseHours || 12;
    const bm = reservationDetails.baseMinutes || 0;
    const ah = reservationDetails.addedHours || 0;
    const am = reservationDetails.addedMinutes || 0;
    let h = bh + ah;
    let m = bm + am;
    if (m >= 60) { h += Math.floor(m / 60); m = m % 60; }
    // clamp to the end of the day grid
    if (h > 23) { h = 23; m = 55; }
    if (h === 23 && m > 55) m = 55;
    return { h, m };
  }, [
    reservationDetails.baseHours, reservationDetails.baseMinutes,
    reservationDetails.addedHours, reservationDetails.addedMinutes
  ]);

  useEffect(() => {
    if (reservation) {
      setClientForm({
        firstname: reservation.client_firstname || '',
        lastname: reservation.client_lastname || '',
        phone: reservation.client_phone || '',
        email: reservation.client_email || '',
        comment: reservation.client_comment || '',
        rate: reservation.client_rate || 3,
        id: reservation.client || null,
      });

    // Directly use the stored Greek time from the database
    const [date, time] = reservation.arrival.split('T'); // No conversion needed
    const formattedTime = time.slice(0, 5); // Extract HH:mm only

    setReservationDetails({
      date: date, // Use the exact date from the DB
      baseHours: parseInt(formattedTime.split(':')[0], 10),
      baseMinutes: parseInt(formattedTime.split(':')[1], 10),
      addedHours: 0,
      addedMinutes: 0,
      people: reservation.people || 2,
      comment: reservation.comment || '',
      reservationTime: formattedTime, // Use database-stored time
    });
    setPeopleDraft(String(reservation.people || 2));

      if (assignedTables) {
        setSelectedTables(assignedTables);
      }
    } else {
      const next = next5SlotToday();
      setReservationDetails({
        date: today,
        baseHours: next.h,
        baseMinutes: next.m,
        addedHours: 0,
        addedMinutes: 0,
        people: 2,
        comment: '',
        reservationTime: next.hhmm
      });
      setPeopleDraft("2");
    }
  }, [reservation, assignedTables]);

  // Calculate and update the reservation time
  const calculateReservationTime = () => {
    const { baseHours, baseMinutes, addedHours, addedMinutes } = reservationDetails;

    let totalHours = baseHours + addedHours;
    let totalMinutes = baseMinutes + addedMinutes;

    if (totalMinutes >= 60) {
      totalHours += Math.floor(totalMinutes / 60);
      totalMinutes %= 60;
    }

    if (totalHours >= 24) {
      totalHours = 23;
      totalMinutes = 59;
    }

    const reservationTime = tr('{{var1}}:{{var2}}', {
      var1: String(totalHours).padStart(2, '0'),
      var2: String(totalMinutes).padStart(2, '0')
    });

    setReservationDetails((prevDetails) => ({
      ...prevDetails,
      reservationTime,
    }));
  };

  // Keep reservationTime string in sync with the computed final time (base + added)
  useEffect(() => {
    // produce final HH:mm from finalHM
    const hh = String(finalHM.h).padStart(2,'0');
    const mm = String(finalHM.m).padStart(2,'0');
    setReservationDetails(prev => {
      return ({
        ...prev,

        reservationTime: tr('{{hh}}:{{mm}}', {
          hh,
          mm
        })
      });
    });
  }, [finalHM.h, finalHM.m]);

  // ---- search helpers (phone vs name) ----
  const digitsOnly = (s) => (s || '').replace(/\D/g, '');

  const startsWithThreeDigits = (s) => /^\s*\d{3}/.test(s || '');

  // Treat as phone if:
  // 1) after 3 chars typed, it *starts with three digits*  OR
  // 2) it has no letters and ?8 digits overall
  const looksLikePhone = (s) => {
    if (!s) return false;
    const raw = String(s);
    if (raw.trim().length >= 3 && startsWithThreeDigits(raw)) return true;
    if (/\p{L}/u.test(raw)) return false;  // letters => treat as name
    const d = digitsOnly(raw);
    return d.length >= 3 && d.length <= 15;
  };

  // Parse "Surname Firstname" (two words, letters/apostrophes/hyphens). Returns { last, first } or null.
  const parseFullName = (s) => {
    const cleaned = (s || '').trim().replace(/\s+/g, ' ');
    // exactly two name-like tokens
    const m = cleaned.match(/^([\p{L}.'-]{2,})\s+([\p{L}.'-]{2,})$/u);
    if (!m) return null;
    return { last: m[1], first: m[2] };
  };

  const handleSearchChange = (e) => {
    const raw = e.target.value ?? '';
    setPhone(raw);                        // < keep what the user typed (spaces included)
    const inputSearch = raw.trim();       // < use trimmed for logic / requests

    const hasLetter = /\p{L}/u.test(inputSearch);
    const enoughChars = hasLetter ? inputSearch.length >= 3 : digitsOnly(inputSearch).length >= 4;

    if (!enoughChars) {
      // reset state...
      setClientOptions([]);
      setShowDropdown(false);
      setClientForm({ firstname:'', lastname:'', phone:'', email:'', comment:'', rate: 3, id:null });
      setIsNewClient(true);
      return;
    }

    const seq = ++clientSearchSeqRef.current;
  
    axios.get(tr('{{API_BASE_URL}}/clients/search/{{var2}}', {
      API_BASE_URL: config.API_BASE_URL,
      var2: encodeURIComponent(inputSearch)
    }))
    .then((response) => {
      // Ignore out-of-order responses while the user types fast
      if (seq !== clientSearchSeqRef.current) return;
  
      const results = response?.data || [];
      const isPhoneLike = looksLikePhone(raw);
  
      // ? NEW: if it's a phone search and only 1 result -> auto-fill the whole client form
      if (isPhoneLike && results.length === 1) {
        handleClientSelect(results[0]);
        setClientOptions([]);
        setShowDropdown(false);
        return;
      }
  
      if (results.length > 0) {
        setIsNewClient(false);
  
        // keep your existing behavior: show dropdown and don't overwrite the input
        setClientOptions(results);
        setShowDropdown(true);
        return;
      }
  
      // ---- No results -> prepare new client draft (your existing behavior) ----
      setClientOptions([]);
      setShowDropdown(false);
  
      const parts = parseFullName(inputSearch);
  
      if (isPhoneLike) {
        // ? this satisfies: "clear all other fields and keep the phone"
        setClientForm({ firstname:'', lastname:'', phone: raw, email:'', comment:'', rate: 3, id:null });
      } else if (parts) {
        setClientForm({ firstname: parts.first, lastname: parts.last, phone:'', email:'', comment:'', rate: 3, id:null });
      } else {
        setClientForm({ firstname:'', lastname: inputSearch, phone:'', email:'', comment:'', rate: 3, id:null });
      }
  
      setIsNewClient(true);
    })
    .catch(() => {
      // Ignore out-of-order responses while the user types fast
      if (seq !== clientSearchSeqRef.current) return;
  
      // On error, still apply the same inference so user can proceed
      const isPhoneLike = looksLikePhone(raw);
      const parts = parseFullName(inputSearch);
  
      setClientOptions([]);
      setShowDropdown(false);
  
      setClientForm({
        firstname: !isPhoneLike && parts ? parts.first : '',
        lastname:  !isPhoneLike && parts ? parts.last  : (!isPhoneLike ? inputSearch : ''),
        phone:     isPhoneLike ? raw : '',
        email: '',
        comment: '',
        rate: 3,
        id: null,
      });

      setIsNewClient(true);
    });
  };

  const handleClientSelect = (client) => {
    setClientForm({
      firstname: client.firstname,
      lastname: client.lastname,
      phone: client.phone,
      email: client.email || '',
      comment: client.comment || '',
      rate: client.rate || 3,
      id: client.id,
    });
    setIsNewClient(false);
    setShowDropdown(false);
  };

  // Αναγνώριση κλήσης (Yealink): φέρνει τον καλούντα της τελευταίας κλήσης στη φόρμα.
  // ΠΟΤΕ αυτόματα — μόνο με το κουμπί ή από το banner («Νέα κράτηση» → ?call=ID).
  const applyPhoneCall = (call) => {
    if (call.client) {
      handleClientSelect(call.client);
    } else {
      setClientForm({ firstname: '', lastname: '', phone: call.number || '', email: '', comment: '', rate: 3, id: null });
      setIsNewClient(true);
    }
    setPhone('');
    setClientOptions([]);
    setShowDropdown(false);
    setMessage(tr('Last caller pulled into the form.'));
    setPulledCallId(call.id);   // → το εικονίδιο σβήνει άμεσα (fade-out)
    setPulledApplied(true);     // → στη θέση του βγαίνει το «×» καθαρισμού
    axios.post(`${config.API_BASE_URL}/phone/calls/${call.id}/used`).catch(() => {});
  };
  // Εικονίδιο τηλεφώνου: κόκκινο όσο υπάρχει κλήση και για 10΄ αφού τελειώσει, γκρι αλλιώς.
  const PHONE_RECENT_MIN = 10;
  const [phoneState, setPhoneState] = useState({ configured: false, call: null });
  useEffect(() => {
    if (reservation) return undefined;
    let stopped = false, timer = null, busy = false;
    const tick = async () => {
      if (stopped || busy) return;
      busy = true;
      let delay = 5000;
      if (document.visibilityState === 'visible') {
        try {
          const { data } = await axios.get(`${config.API_BASE_URL}/phone/last-call?within=${PHONE_RECENT_MIN}`);
          setPhoneState({ configured: !!data?.configured, call: data?.call || null });
          if (!data?.configured) delay = 60000;
        } catch (e) {
          // ΠΟΤΕ οριστικό σταμάτημα: ένα 401/403 (λήξη session, στιγμιαίο rate limit) πάγωνε
          // το εικονίδιο μέχρι το επόμενο reload — απλώς αραιώνουμε τον ρυθμό.
          delay = (e?.response?.status === 401 || e?.response?.status === 403) ? 300000 : 30000;
        }
      }
      busy = false;
      if (stopped) return;
      clearTimeout(timer);
      timer = setTimeout(tick, delay);
    };
    tick();
    // Οι χρονομετρητές κρυφών καρτελών παγώνουν (tablet στο πάσο, εναλλαγή εφαρμογών):
    // με την επιστροφή στην καρτέλα ζητάμε αμέσως φρέσκια κατάσταση.
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [reservation]);
  // Μόλις ο χρήστης τραβήξει μια κλήση στη φόρμα, το εικονίδιο έχει κάνει τη δουλειά του:
  // σβήνει αμέσως με fade-out αντί να μείνει κόκκινο για 10΄. Κρατάμε ποιο id τραβήχτηκε,
  // ώστε το polling να μην το ξαναφέρνει στην επόμενη ανανέωση.
  const [pulledCallId, setPulledCallId] = useState(null);
  // Αληθές όσο η φόρμα κρατά στοιχεία που ήρθαν από κλήση: τότε στη θέση του τηλεφώνου
  // εμφανίζεται ένα «×» για καθάρισμα, αν ο πελάτης που ταίριαξε δεν είναι ο σωστός.
  const [pulledApplied, setPulledApplied] = useState(false);
  const phoneCall = phoneState.call && phoneState.call.id !== pulledCallId ? phoneState.call : null;
  const phoneLive = !!phoneCall && (phoneCall.event === 'incoming' || phoneCall.event === 'established');
  const phoneTitle = !phoneState.configured
    ? tr('Caller ID is not set up. Ask your administrator (Settings → Phone).')
    : phoneCall
      ? `${phoneLive ? tr('Calling now') : tr('Last call')}: ${phoneCall.client ? `${phoneCall.client.lastname || ''} ${phoneCall.client.firstname || ''}`.trim() : phoneCall.number}`
      : tr('No recent call.');
  // Καθαρίζει ό,τι έφερε η κλήση, ώστε ο χρήστης να ψάξει πελάτη κανονικά. Η κλήση παραμένει
  // «τραβηγμένη», δηλαδή το τηλεφωνάκι ΔΕΝ ξαναβγαίνει γι' αυτήν — μόνο για επόμενη κλήση.
  const clearPulledForm = () => {
    setClientForm({ firstname: '', lastname: '', phone: '', email: '', comment: '', rate: 3, id: null });
    setIsNewClient(true);
    setPhone('');
    setClientOptions([]);
    setShowDropdown(false);
    setMessage('');
    setError('');
    setPulledApplied(false);
  };
  const pullLastCall = async () => {
    try {
      const { data } = await axios.get(`${config.API_BASE_URL}/phone/last-call?within=${PHONE_RECENT_MIN}`);
      if (!data?.call) {
        setError(tr(data?.configured ? 'No recent call.' : 'Caller ID is not set up. Ask your administrator (Settings → Phone).'));
        return;
      }
      setError('');
      applyPhoneCall(data.call);
    } catch {
      setError(tr('Something went wrong — please try again.'));
    }
  };
  useEffect(() => {
    if (reservation) return;
    const id = new URLSearchParams(window.location.search).get('call');
    if (!id) return;
    axios.get(`${config.API_BASE_URL}/phone/calls/${id}`)
      .then(({ data }) => { if (data?.call) applyPhoneCall(data.call); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClientFormChange = (e) => {
    const { name, value } = e.target;
    setClientForm({ ...clientForm, [name]: value });
  };

  const handleReservationFormChange = (e) => {
    const { name, value } = e.target;
    setReservationDetails((prevDetails) => ({
      ...prevDetails,
      [name]: parseInt(value, 10) || 0,
    }));
  };

  const handleTablesSelected = (tables) => {
    setSelectedTables(tables);
  };

  const saveClientInfo = () => {
    if (!isClientIdentified) {
      setError('Please provide at least one of: phone, first name, or last name.');
      return;
    }

    //console.log('isNewClient', isNewClient)

    if (isNewClient && !clientForm.id) {
      console.log('New clientForm', clientForm)
      axios
        .post(tr('{{API_BASE_URL}}/clients', {
        API_BASE_URL: config.API_BASE_URL
      }), clientForm)
        .then((response) => {
          const createdClient = response.data;
          setClientForm(createdClient);
          setIsNewClient(false);
          setMessage('New client saved successfully.');
          setError('');
        })
        .catch(() => {
          setError('Failed to save new client.');
        });
    } else {
      console.log('Existing clientForm', clientForm)
      axios
        .put(tr('{{API_BASE_URL}}/clients/{{id}}', {
        API_BASE_URL: config.API_BASE_URL,
        id: clientForm.id
      }), clientForm)
        .then(() => {
          setMessage('Client updated successfully.');
          setError('');
        })
        .catch(() => {
          setError('Failed to update client.');
        });
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
  
    if (isSaving) return;         
    setIsSaving(true);
  
    setError('');
    setMessage('');

    // For CREATE only, require at least one identifier
    if (!reservation && !isClientIdentified) {
      setError('Please provide at least one of: phone, first name, or last name.');
      setIsSaving(false);
      return;
    }

    try {
      // 1) Client id resolution
      let clientId = clientForm.id ?? reservation?.client ?? null;
      if (!reservation && !clientId) {
        // On CREATE only, auto-create a client if needed
        const resp = await axios.post(tr('{{API_BASE_URL}}/clients', {
          API_BASE_URL: config.API_BASE_URL
        }), {
          firstname: clientForm.firstname || '',
          lastname:  clientForm.lastname  || '',
          phone:     clientForm.phone     || '',
          email:     clientForm.email     || '',
          comment:   clientForm.comment   || '',
          rate:      clientForm.rate      || 3,
        });
        const createdClient = resp.data;
        clientId = createdClient.id;
        setClientForm(createdClient);
        setIsNewClient(false);
      }

      // 2) Build reservation payload
      const reservationData = {
        client: clientId,
        arrival: tr('{{date}}T{{reservationTime}}', {
          date: reservationDetails.date,
          reservationTime: reservationDetails.reservationTime
        }),
        people: reservationDetails.people,
        comment: reservationDetails.comment,
        order: 0,
      };

      // 3) update reservation
      if (reservation && reservation.id) {
        await axios.put(tr('{{API_BASE_URL}}/reservations/{{id}}', {
          API_BASE_URL: config.API_BASE_URL,
          id: reservation.id
        }), reservationData);
        // Assign tables if any selected
        await axios.post(tr('{{API_BASE_URL}}/assignedtables', {
          API_BASE_URL: config.API_BASE_URL
        }), {
          reservationId: reservation.id,
          tables: Array.isArray(selectedTables) ? selectedTables : [],
        });
        setMessage('Reservation updated successfully.');
        onClose();
        return;
      }

      // Create new reservation
      const res = await axios.post(tr('{{API_BASE_URL}}/reservations', {
        API_BASE_URL: config.API_BASE_URL
      }), reservationData);
      const createdReservation = res.data;

      if (selectedTables.length > 0) {
        try {
          await axios.post(tr('{{API_BASE_URL}}/assignedtables', {
            API_BASE_URL: config.API_BASE_URL
          }), {
            reservationId: createdReservation.id,
            tables: selectedTables,
          });
          setMessage('Reservation and table assignments created successfully.');
        } catch {
          setError('Reservation saved, but failed to assign tables.');
        }
      } else {
        setMessage('Reservation created successfully.');
      }

      setCreatedReservation(createdReservation);
      if (planId === 1) {
        // Starter plan: skip AssignMeats, go straight back to list
        navigate('/reservations-list');
      } else {
        // Other plans: open AssignMeats modal as before
        setIsMeatsModalOpen(true);
      }
    } catch (err) {
      // client creation or reservation creation/update failed
      setError('Failed to save reservation (or client). Please try again.');
    } finally {
      if (mountedRef.current) setIsSaving(false);
    }
  };

  const calenderStep = (step) => {
    const datetime = new Date(availabilityDate);
    return new Date(datetime.setDate(datetime.getDate()+step)).toISOString().split('T')[0];
  };

  const visibleTables = availabilityData.filter(t => areaMatchesFilters(t.area) && (selectedAreaId==null || Number(t.area)===Number(selectedAreaId)));

  const visibleTablesCount = visibleTables.length;

  const calculateSlotAvailability = (i) => {
    const totalTables = visibleTables.length || 1;
    let reservedTables = 0;
  
    visibleTables.forEach((table) => {
      const hasReservation = table.reservations.some((res) => {
        const resHour = +res.arrival.slice(11, 13);
        const resMinute = +res.arrival.slice(14, 16);
        const resIndex = (resHour - 12) * 12 + Math.floor(resMinute / 5); // Convert time to index
  
        const durationSlots = Math.max(1, Math.ceil((reservationDurationMinutes || 120) / 5)); // 5-min slots
        return resIndex <= i && i < resIndex + durationSlots;
      });
  
      if (hasReservation) {
        reservedTables++;
      }
    });
  
    return (reservedTables / totalTables) * 100; 
  };

  // ===== Mini capacity graph header helpers =====
  const pct = (num, den, decimals = 0) => {
    const n = Number(num) || 0;
    const d = Number(den) || 0;
    if (!d) return 0;
    const v = (100 * n) / d;
    return decimals > 0 ? Number(v.toFixed(decimals)) : Math.round(v);
  };
  
  // % of the graph area "used/orange" = integral(value) / (capacity * slotsCount)
  const getAreaUsedPercent = (graph) => {
    if (!graph?.slots?.length || !graph.totalCapacity) return 0;
    const sum = graph.slots.reduce((acc, s) => acc + (Number(s.value) || 0), 0);
    const den = graph.totalCapacity * graph.slots.length;
    return pct(sum, den, 1); // 1 decimal looks nice
  };
  
  const getMaxInfo = (graph) => {
    if (!graph?.slots?.length) return { time: "-", value: 0, percent: 0 };
    let best = graph.slots[0];
    for (const s of graph.slots) {
      if ((Number(s.value) || 0) > (Number(best.value) || 0)) best = s;
    }
    const v = Number(best.value) || 0;
    return {
      time: best.label || "-",
      value: v,
      percent: pct(v, graph.totalCapacity, 0),
    };
  };


  return (
    <div ref={formRootRef} className={forceBottomSelector ? 'rf-force-bottom' : ''} style={{marginLeft: isMobile?'10px':'30px'}}>
      <h2>
        <span>{tr('Reservation')} &#8226; {reservation ? reservation.id : tr('Form')}</span>
        {!isMobile && (
          <button
            type="button"
            onClick={() => {
              const targetDate = reservationDetails.date || todayLocalISO();
          
              setAvailabilityDate(targetDate);
              fetchAvailability(targetDate);
          
              setReservationDetails(prev => ({ ...prev, addedHours: 0, addedMinutes: 0 }));
              setShowAvailabilityModal(true);
            }}
            style={{ marginLeft: '340px', width: '145px'}}
          >{tr('Availability Calendar')}</button>
        )}
      </h2>
      {/* Audit (only existing reservations) */}
      {reservation?.id && (
        <div style={{fontSize:10, color:'#555', margin:'5px'}}>
          {reservation.created && (
            <div>
              <strong>{tr('Created:')}</strong>{' '}
              {formatDateTimeGR(reservation.created)}{' '}
              {reservation.created_by_username ? tr('  {{created_by_username}}', {
                created_by_username: reservation.created_by_username
              }) : ''}
            </div>
          )} 
          {reservation.updated && (
            <div>
              <strong>{tr('Updated:')}</strong>{' '}
              {formatDateTimeGR(reservation.updated)}{' '}
              {reservation.updated_by_username ? tr('  {{updated_by_username}}', {
                updated_by_username: reservation.updated_by_username
              }) : ''}
            </div>
          )}
        </div>
      )}
      {message && <p style={{ color: 'green' }}>{message}</p>}
      {error && <p style={{ color: 'red' }}>{error}</p>}
      <div className="rf-mainrow">
      
        <div className="rf-col-left">
          {!reservation &&
            <div style={{ position: "relative", display: "inline-block" }}>
              <label htmlFor="searchClients">{tr('Search Clients:')}{' '}</label>
            
              {/* Αναζήτηση + εικονίδιο τηλεφώνου: το εικονίδιο εμφανίζεται (fade-in) μόνο όσο υπάρχει
                  πρόσφατη κλήση· αλλιώς εξαφανίζεται και η αναζήτηση παίρνει όλο το πλάτος. */}
              <span className="rf-search-row">
                <input
                  id="searchClients"
                  type="text"
                  name="search"
                  value={phone || ""}
                  onChange={handleSearchChange}
                  onFocus={() => { if (clientOptions.length > 0) setShowDropdown(true); }}
                  onBlur={() => { setTimeout(() => setShowDropdown(false), 150); }}
                  placeholder={tr("By phone, name or email")}
                  required
                  style={{ flex: '1 1 auto', minWidth: 0, padding: "3px", boxSizing: "border-box" }}
                />
                {clientOptions.length > 0 && <i style={{ color: "darkBlue", marginLeft: 4 }}>#{clientOptions.length}</i>}
                <button type="button" onClick={pullLastCall} title={phoneTitle} aria-label={phoneTitle}
                  tabIndex={phoneCall ? 0 : -1}
                  className={`rf-phone-btn ${phoneLive ? 'is-live' : phoneCall ? 'is-recent' : 'is-idle'}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                </button>
                {!phoneCall && pulledApplied && (
                  <button type="button" onClick={clearPulledForm}
                    title={tr('Clear the form')} aria-label={tr('Clear the form')}
                    className="rf-clear-btn">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
                      strokeLinecap="round" aria-hidden="true">
                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
              </span>
            
              {showDropdown && clientOptions.length > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    width: "195px",
                    background: "white",
                    border: "1px solid #ccc",
                    borderRadius: 6,
                    marginTop: 4,
                    maxHeight: 260,
                    overflowY: "auto",
                    zIndex: 9999,
                  }}
                >
                  {clientOptions.slice(0, 15).map((client) => (
                    <div
                      key={client.id}
                      onMouseDown={(e) => e.preventDefault()} // prevent blur before click
                      onClick={() => {
                        handleClientSelect(client);
                        setPhone("");          // clear so user can type a new phone immediately
                        setClientOptions([]);  // optional
                        setShowDropdown(false);
                      }}
                      style={{
                        padding: "6px 8px",
                        cursor: "pointer",
                        borderBottom: "1px solid #eee",
                        fontSize: 13,
                      }}
                    >
                      <b>{(client.lastname || "") + " " + (client.firstname || "")}</b>
                      <div style={{ fontSize: 12, opacity: 0.7 }}>{client.phone || ""}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          }
  
          <h3>{tr('Client Information')}<button
              type="button"
              onClick={saveClientInfo}
              disabled={!isClientIdentified}
              style={{marginLeft: '10px', width: '135px'}}
            >
              {clientForm && clientForm.id ? tr('Update') : tr('Insert')}{' '}{tr('Client')}</button>
          </h3>
          <table style={{ width: '100%', maxWidth: 300, borderCollapse: 'collapse'}}>
            <tbody>
              <tr>
                <td style={{ textAlign: 'right', padding: '3px' }}>
                  <label htmlFor="phone">{tr('Phone:')}</label>
                </td>
                <td style={{ textAlign: 'left', padding: '3px' }}>
                  <input
                    id="phone"
                    type="tel"
                    name="phone"
                    value={clientForm.phone}
                    onChange={handleClientFormChange}
                    placeholder={tr('Enter phone number')}
                    style={{ width: '94%' }}
                  />
                </td>
              </tr>
              <tr>
                <td style={{ textAlign: 'right', padding: '3px' }}>
                  <label htmlFor="lastname">{tr('Last Name:')}</label>
                </td>
                <td style={{ textAlign: 'left', padding: '3px' }}>
                  <input
                    id="lastname"
                    type="text"
                    name="lastname"
                    value={clientForm.lastname}
                    onChange={handleClientFormChange}
                    placeholder={tr('Enter last name')}
                    style={{ width: '94%' }}
                  />
                </td>
              </tr>              
              <tr>
                <td style={{ textAlign: 'right', padding: '3px' }}>
                  <label htmlFor="firstname">{tr('First Name:')}</label>
                </td>
                <td style={{ textAlign: 'left', padding: '3px' }}>
                  <input
                    id="firstname"
                    type="text"
                    name="firstname"
                    value={clientForm.firstname}
                    onChange={handleClientFormChange}
                    placeholder={tr('Enter first name')}
                    style={{ width: '94%' }}
                  />
                </td>
              </tr>
              <tr style={{opacity: clientForm.email ? 1:'0.6'}}>
                <td style={{ textAlign: 'right', padding: '3px' }}>
                  <label htmlFor="email">{tr('Email:')}</label>
                </td>
                <td style={{ textAlign: 'left', padding: '3px' }}>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={clientForm.email}
                    onChange={handleClientFormChange}
                    placeholder={tr('Enter email address')}
                    style={{ width: '94%' }}
                  />
                </td>
              </tr>
              <tr style={{opacity: clientForm.comment ? 1:'0.6'}}>
                <td style={{ textAlign: 'right', padding: '3px' }}>
                  <label htmlFor="clientcomment">{tr('Comment:')}</label>
                </td>
                <td style={{ textAlign: 'left', padding: '3px' }}>
                  <textarea
                    id="clientcomment"
                    name="comment"
                    value={clientForm.comment}
                    onChange={handleClientFormChange}
                    placeholder={tr('Enter comment')}
                    style={{ width: '94%' , transition: '.5s', height: isMobile ? "17px" : (clientForm.id && historyTotals) ? "24px" : "30px"}}
                  />
                </td>
              </tr>
              <tr>
                <td style={{ textAlign: 'right', padding: '3px' }}>
                  <label>{tr('Rating:')}</label>
                </td>
                <td style={{ textAlign: 'left', padding: '3px', color: 'darkOrange' }}>
                  {[1, 2, 3, 4, 5].map((n) => {
                    const current = clientForm.rate ?? 3;
                    const filled = n <= current;
                    return (
                      <span
                        key={n}
                        onClick={() => setClientForm({ ...clientForm, rate: n })}
                        style={{
                          cursor: 'pointer',
                          fontSize: 20,
                          color: filled ? '#f5b301' : '#ccc',
                          userSelect: 'none',
                          marginLeft: 10,
                          marginRight: 10,
                        }}
                        title={tr('{{n}} star{{var2}}', {
                          n,
                          var2: n > 1 ? 's' : ''
                        })}
                      >
                        {filled ? '\u2605' : '\u2606'}
                      </span>
                    );
                  })}
                </td>
              </tr>
              {clientForm.id && (
                <tr>
                  <td style={{ textAlign: "right", padding: "3px" }}>
                    <label>{tr("History:")}</label>
                  </td>
                  <td style={{ textAlign: "left", padding: "3px" }}>
                    <ClientsHistory
                      clientId={clientForm.id}
                      clientName={
                        [clientForm.lastname, clientForm.firstname].filter(Boolean).join(" ").trim() ||
                        clientForm.name ||
                        ""
                      }
                      prefetch={true}
                      onTotals={setHistoryTotals}
                      showInlineTrigger={true}
                      showInlineTotals={true}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
  
        <div className="rf-col-right">
        
        {isMobile && (<hr className="rf-below-divider" style={{width: '100%', maxWidth: 750, marginLeft: 0}} />)}
        
          <div className="rf-graph-wrap">
            {/* Mini capacity graph for the selected date (Recharts) */}
            {seatGraphData &&
             seatGraphData.totalCapacity > 0 &&
             seatGraphData.slots.length > 0 && (
              <div className="rf-capacity-chart">
                <div className="rf-capacity-chart-header">
                  {(() => {
                    const areaUsed = getAreaUsedPercent(seatGraphData);
                    const mx = getMaxInfo(seatGraphData);
                
                    const selTime = reservationDetails?.reservationTime || "-";
                    const selSeats = Number(selectedSlotForGraph?.value) || 0;
                    const selPct = pct(selSeats, seatGraphData.totalCapacity, 0);
                
                    return (
                      <span style={{ width: "100%" }}>
                        {tr('{{date}}: {{areaUsed}}% - Max @{{maxTime}}: {{maxSeats}} ({{maxPct}}%) - Selection @{{selTime}}: {{selSeats}} ({{selPct}}%)', {
                          date: reservationDetails.date,
                          areaUsed,
                          maxTime: mx.time,
                          maxSeats: mx.value,
                          maxPct: mx.percent,
                          selTime,
                          selSeats,
                          selPct,
                        })}
                      </span>
                    );
                  })()}
                </div>


                <div className="rf-graph-canvas">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={seatGraphData.slots}
                      margin={{ top: 5, right: 10, left: 0, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                  
                      <XAxis
                        dataKey="label"
                        // show tick every hour (index 0,12,24,...) because 12 ? 5' = 1h
                        ticks={seatGraphData.slots
                          .filter((_, idx) => idx % 12 === 0)
                          .map((slot) => slot.label)}
                        tickFormatter={(label) => {
                          // label is "HH:MM" > we only want "HH" without leading zero
                          const hour = parseInt(label.slice(0, 2), 10);
                          return String(hour);
                        }}
                        interval={0}
                        tick={{ fontSize: 8 }}
                      />
                  
                      <YAxis
                        domain={[0, seatGraphData.totalCapacity]}
                        allowDecimals={false}
                        tick={{ fontSize: 8 }}
                      />
                  
                      <Tooltip
                        formatter={value => {
                          return [tr('{{value}} seats', {
                            value
                          }), "Reserved"];
                        }}
                        labelFormatter={(label) => label}
                      />
                  
                      {/* vertical line at current reservation time */}
                      {selectedSlotForGraph && (
                        <ReferenceLine
                          x={selectedSlotForGraph.label}
                          stroke="blue"
                          strokeWidth={1}
                        />
                      )}
                  
                      {/* capacity line */}
                      <ReferenceLine
                        y={seatGraphData.totalCapacity}
                        stroke="#999"
                        strokeDasharray="3 3"
                      />
                  
                      {/* FILLED AREA under the data line */}
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke="#ff7300"
                        strokeWidth={1}
                        fill="darkRed"
                        fillOpacity={0.2}   
                        isAnimationActive={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>

          
          <h3 style={{marginTop: '-30px', marginBottom: '10px'}}>{tr('Reservation')}<button
              type="submit"
              onClick={handleSubmit}
              disabled={isSaving || (!reservation && !isClientIdentified)}
              style={{
                marginLeft: '40px',
                padding: 5,
                borderRadius: 10,
                width: '160px',
                position: 'relative', 
                zIndex: 5,
                opacity: isSaving ? 0.6 : 1,
                cursor: isSaving ? 'not-allowed' : 'pointer',
                color: (!reservation && !isClientIdentified) ? 'darkGray' : (isMobile ? 'orange' : 'orangered'),
                fontWeight: 'bold'
              }}
               title={
                (!isClientIdentified)
                  ? 'Provide at least one of: phone, first name, or last name'
                  : (reservation ? 'Update reservation' : 'Create reservation (client will be auto-saved)')
                }
              >
              {isSaving ? tr('Saving') : (reservation ? tr('Update') : tr('Create'))}{' '}{tr('Reservation')}
            </button>
          </h3>
          <table style={{ width: '100%', maxWidth: 300, borderCollapse: 'collapse'}}>
            <tbody>
              <tr>
                <td style={{ padding: '5px'}}>
                  <b aria-hidden="true">{'\uD83D\uDCC5'}</b>
                  <Flatpickr
                    value={reservationDetails.date ? parseYmdLocal(reservationDetails.date) : null}
                    options={{
                      dateFormat: "Y-m-d",       // stored as ISO
                      altInput: true,            // show formatted version to the user
                      altFormat: "l, d/m/Y",     // e.g. Wednesday, 30/07/2025
                      minDate: "today",          // prevent past dates
                      altInputClass: "custom-flatpickr", // attach a class
                      locale: isGreek ? Greek : undefined,
                    }}
                     onChange={([selectedDate]) => {
                       if (!selectedDate) return;
                       const year  = selectedDate.getFullYear();
                       const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
                       const day   = String(selectedDate.getDate()).padStart(2, "0");
                       const ymd   = tr('{{year}}-{{month}}-{{day}}', {
                         year,
                         month,
                         day
                       });

                       // If date is today > next 5' slot (but not before 12:00)
                       // If future > 12:00
                       const today = new Date();
                       let nextTime = { h: 12, m: 0, hhmm: "12:00" };
                       if (isSameLocalDay(selectedDate, today)) {
                         nextTime = next5SlotToday();
                       }

                       setReservationDetails(prev => ({
                         ...prev,
                         date: ymd,
                         baseHours: nextTime.h,
                         baseMinutes: nextTime.m,
                         addedHours: 0,
                         addedMinutes: 0,
                         reservationTime: nextTime.hhmm,
                       }));
                     }}
                  />
                  <b aria-hidden="true" style={{marginLeft: '10px'}}>{'\uD83D\uDC68'}</b>
                  {isMobile ? (
                    <span className="rf-people-stepper">
                      <button
                        type="button"
                        className="rf-people-btn"
                        onClick={() => {
                          const cur = parseInt(peopleDraft, 10);
                          const base = Number.isFinite(cur) ? cur : (Number(reservationDetails.people) || 1);
                          const next = Math.max(1, base - 1);
                          setPeopleDraft(String(next));
                          setReservationDetails((prev) => ({ ...prev, people: next }));
                        }}
                        aria-label="Decrease people"
                      >
                        -
                      </button>
                    
                      <input
                        id="people"
                        type="number"
                        inputMode="numeric"
                        value={peopleDraft}
                        onChange={(e) => {
                          const s = e.target.value;
                    
                          // allow empty while typing (so the "1" does NOT snap back)
                          if (s === "") {
                            setPeopleDraft("");
                            setReservationDetails((prev) => ({ ...prev, people: 0 }));
                            return;
                          }
                    
                          // allow digits only
                          if (!/^\d+$/.test(s)) return;
                    
                          setPeopleDraft(s);
                          setReservationDetails((prev) => ({ ...prev, people: parseInt(s, 10) || 0 }));
                        }}
                        onBlur={() => {
                          const v = parseInt(peopleDraft, 10);
                          const fixed = Number.isFinite(v) ? Math.max(1, v) : 1;
                          setPeopleDraft(String(fixed));
                          setReservationDetails((prev) => ({ ...prev, people: fixed }));
                        }}
                        min={1}
                        max={1000}
                        style={{ width: '52px', padding: '5px', boxSizing: 'border-box', textAlign: 'center' }}
                      />
                    
                      <button
                        type="button"
                        className="rf-people-btn"
                        onClick={() => {
                          const cur = parseInt(peopleDraft, 10);
                          const base = Number.isFinite(cur) ? cur : (Number(reservationDetails.people) || 1);
                          const next = Math.max(1, base + 1);
                          setPeopleDraft(String(next));
                          setReservationDetails((prev) => ({ ...prev, people: next }));
                        }}
                        aria-label="Increase people"
                      >
                        +
                      </button>
                    </span>
                  ) : (
                    <input
                      id="people"
                      type="number"
                      inputMode="numeric"
                      value={peopleDraft}
                      onChange={(e) => {
                        const s = e.target.value;
                        if (s === "") {
                          setPeopleDraft("");
                          setReservationDetails((prev) => ({ ...prev, people: 0 }));
                          return;
                        }
                        if (!/^\d+$/.test(s)) return;
                        setPeopleDraft(s);
                        setReservationDetails((prev) => ({ ...prev, people: parseInt(s, 10) || 0 }));
                      }}
                      onBlur={() => {
                        const v = parseInt(peopleDraft, 10);
                        const fixed = Number.isFinite(v) ? Math.max(1, v) : 1;
                        setPeopleDraft(String(fixed));
                        setReservationDetails((prev) => ({ ...prev, people: fixed }));
                      }}
                      min={1}
                      max={1000}
                      style={{ width: '59px', padding: '5px', boxSizing: 'border-box' }}
                    />
                  )}  
                </td>
              </tr>
              <tr>
                <td className="rf-time-inline" style={{ padding: '5px' }}>
                  <b aria-hidden="true">{'\uD83D\uDD51'}</b>
                  {/* Hour + Minute dropdowns */}
                  <select
                    value={finalHM.h}
                    onChange={handleHourChange}
                    style={{ width: 50, padding: '5px', boxSizing: 'border-box', textAlign: 'center' }}
                  >
                    {HOURS.map(h => (
                      <option key={h} value={h}>{String(h).padStart(2, '0')}</option>
                    ))}
                  </select>
                  <b> : </b>
                  <select
                    value={finalHM.m} 
                    onChange={handleMinuteChange}
                    style={{ width: 50, padding: '5px', boxSizing: 'border-box', textAlign: 'center' }}
                  >
                    {MINUTES.map(m => (
                      <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                    ))}
                  </select>
                  <div className="rf-added" style={{opacity: (reservationDetails.addedHours || reservationDetails.addedMinutes ? 1:'0.5')}}>
                    <b style={{marginLeft: 10}}> + </b>
                    {/* Added time dropdowns */}
                    <select
                      id="addedHours"
                      name="addedHours"
                      value={reservationDetails.addedHours}
                      onChange={handleAddedHoursChange}
                      style={{ width: 45, padding: '5px', boxSizing: 'border-box', textAlign: 'center' }}
                    >
                      {ADDED_HOURS.map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                    <span> : </span>
                    <select
                      id="addedMinutes"
                      name="addedMinutes"
                      value={reservationDetails.addedMinutes}
                      onChange={handleAddedMinutesChange}
                      style={{ width: 50, padding: '5px', boxSizing: 'border-box', textAlign: 'center' }}
                    >
                      {ADDED_MINUTES.map(m => (
                        <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                      ))}
                    </select>   
                  </div>
                </td>
              </tr>
              <tr>
                <td style={{ padding: '5px', display: 'flex', opacity: reservationDetails.comment ? 1:'0.7' }}>
                  <b aria-hidden="true" style={{color: 'lightBlue'}}>{'\uD83D\uDDE8'}</b>
                  <textarea
                    id="comment"
                    name="comment"
                    value={reservationDetails.comment}
                    onChange={(e) =>
                      setReservationDetails({ ...reservationDetails, comment: e.target.value })
                    }
                    placeholder={tr('Set notes for this reservation')}
                    style={{ marginLeft: '10px', width: '100%', padding: '5px', boxSizing: 'border-box', height: isMobile?'23px':'70px' }}
                  />
                </td>
              </tr>
            </tbody>
          </table>
  
        </div>
   
        {/* RIGHT-HAND copy (hidden on narrow screens by CSS) */}
        <aside className="rf-selector-right">
          {/* small vertical divider handled by CSS */}
          <div className="rf-selector-inner" style={{ width: '100%', maxWidth: 750 }}>
            {/* ----- Area Filters (same as below) ----- */}
            <div className="ts-area-filters" style={{ margin: '10px 0 6px 0', display: 'ruby' }}>
              <h4 className="ts-area-filters-title" style={{ margin: '3px' }}>
                {tr('Area Filters:')}
              </h4>
            
              <div className="ts-area-filters-items" style={{ marginTop: 6, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {visibleFilters.map(([key, label]) => (
                  <label key={key} style={{ userSelect: 'none', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!areaFilters[key]}
                      onChange={(e) => setAreaFilters(prev => ({ ...prev, [key]: e.target.checked }))}
                      style={{ marginRight: 6, position: 'relative', top: '5px' }}
                    />
                    {tr(label)}
                  </label>
                ))}
              </div>
            </div>
        
            {/* ----- TableSelector (same props as below) ----- */}
           {/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(
               tr('{{date}}T{{reservationTime}}', {
                 date: reservationDetails.date,
                 reservationTime: reservationDetails.reservationTime
               })
             ) && (            
            <TableSelector
              key={`right-${selectorKey}`}
              reservationTime={tr('{{date}}T{{reservationTime}}', {
                date: reservationDetails.date,
                reservationTime: reservationDetails.reservationTime
              })}
              selectedAssignedTables={selectedTables}
              onTablesSelected={handleTablesSelected}
              areaFilter={(area) => areaMatchesFilters(area.id)}
              tableFilter={(table) => areaMatchesFilters(table.area)}
              onAreaTabChange={(areaId) => setSelectedAreaId(areaId)}
              people={livePeople}
              activeReservationId={reservation?.id || null}
            />
           )}
          </div>
        </aside>   
        
      </div>

      <aside className="rf-selector-bottom">
        <div
          style={
            isMobile
              ? {
                  width: '100vw',
                  marginLeft: 'calc(50% - 50vw)',
                  marginRight: 'calc(50% - 50vw)',
                  padding: '0 8px',
                  boxSizing: 'border-box',
                }
              : { width: '100%', maxWidth: 750 }
          }
        >
          <hr className="rf-below-divider" style={{ width: '100%', marginLeft: 0 }} />
      
          {/* Area Filters */}
          <div className="ts-area-filters" style={isMobile ? undefined : { margin: '10px 0 6px 0', display: 'ruby' }}>
            <h4 className="ts-area-filters-title" style={isMobile ? undefined : { margin: '3px' }}>
              {tr('Area Filters:')}
            </h4>
      
            {isMobile ? (
              <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                <ScrollRow className="rf-area-filters-scroll" resetKey={visibleFilters.length} ariaLabel="Area filters">
                  {visibleFilters.map(([key, label]) => (
                    <label key={key} style={{ userSelect: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      <input
                        type="checkbox"
                        checked={!!areaFilters[key]}
                        onChange={(e) => setAreaFilters((prev) => ({ ...prev, [key]: e.target.checked }))}
                        style={{ marginRight: 6 }}
                      />
                      {tr(label)}
                    </label>
                  ))}
                </ScrollRow>
              </div>
            ) : (
              <div
                className="ts-area-filters-items"
                style={{ marginTop: 6, display: 'flex', gap: 12, flexWrap: 'wrap' }}
              >
                {visibleFilters.map(([key, label]) => (
                  <label key={key} style={{ userSelect: 'none', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!areaFilters[key]}
                      onChange={(e) => setAreaFilters((prev) => ({ ...prev, [key]: e.target.checked }))}
                      style={{ marginRight: 6, position: 'relative', top: '5px' }}
                    />
                    {tr(label)}
                  </label>
                ))}
              </div>
            )}

          </div>
      
          {/* TableSelector */}
          <div style={{ width: '100%' }}>
            {/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(
              tr('{{date}}T{{reservationTime}}', {
                date: reservationDetails.date,
                reservationTime: reservationDetails.reservationTime,
              })
            ) && (
              <TableSelector
                key={`bottom-${selectorKey}`}
                reservationTime={tr('{{date}}T{{reservationTime}}', {
                  date: reservationDetails.date,
                  reservationTime: reservationDetails.reservationTime,
                })}
                selectedAssignedTables={selectedTables}
                onTablesSelected={handleTablesSelected}
                areaFilter={(area) => areaMatchesFilters(area.id)}
                tableFilter={(table) => areaMatchesFilters(table.area)}
                onAreaTabChange={(areaId) => setSelectedAreaId(areaId)}
                people={livePeople}
              activeReservationId={reservation?.id || null}
              />
            )}
          </div>
        </div>
      </aside>

            <AvailabilityCalendar
              showAvailabilityModal={showAvailabilityModal}
              onClose={() => setShowAvailabilityModal(false)}
              availabilityDate={availabilityDate}
              setAvailabilityDate={setAvailabilityDate}
              today={today}
              parseYmdLocal={parseYmdLocal}
              isGreek={isGreek}
              tr={tr}
              visibleFilters={visibleFilters}
              areaFilters={areaFilters}
              setAreaFilters={setAreaFilters}
              isMobile={isMobile}
              calendarInfo={calendarInfo}
              calenderStep={calenderStep}
              fetchAvailability={fetchAvailability}
              loadingAvailability={loadingAvailability}
              visibleTables={visibleTables}
              visibleTablesCount={visibleTablesCount}
              calculateSlotAvailability={calculateSlotAvailability}
              reservation={reservation}
              reservationDurationSlots={reservationDurationSlots}
              reservationBlockWidth={reservationBlockWidth}
              hoverBlockWidth={hoverBlockWidth}
              containerRef={containerRef}
              tbodyRef={tbodyRef}
              hoverRef={hoverRef}
              setReservationDetails={setReservationDetails}
              setSelectedTables={setSelectedTables}
              next5SlotToday={next5SlotToday}
              isSameLocalDay={isSameLocalDay}
            />
      <Modal
        isOpen={isMeatsModalOpen && !!createdReservation}
        onRequestClose={handleCloseMeatsModal}
        style={{
          overlay: {
            backgroundColor: 'rgba(0,0,0,0.35)',
            zIndex: 1000
          },
          content: {
                inset: '0px',
                margin: 'auto',
                padding: '8px 10px',
                border: '0',
                borderRadius: 10,
                width: '88vw',
                maxWidth: '88vw',
                height: 'fit-content',
                maxHeight: '80vh',
                overflow: 'auto'
              }
        }}
      >
        {createdReservation && (
          <AssignMeats
            reservation={createdReservation}
            // close after saving or closing; navigating refreshes ReservationsList
            onCloseModal={handleCloseMeatsModal}
          />
        )}
      </Modal>
    </div>
  );
};

export default ReservationForm;
